/**
 * Inventory search — find a catalogue item by the name people actually use.
 *
 * One matcher for every inventory search box: the client's three paths and the
 * rep panel. A client typing "couch" and a rep typing "couch" must get the same
 * answer, or the list the client built is not the list the rep can find.
 *
 * ---------------------------------------------------------------------------
 * HOW A QUERY MATCHES
 * ---------------------------------------------------------------------------
 *   normalise   case, punctuation, hyphens and simple plurals go: "L-Shape
 *               Sofas!" and "l shape sofa" are the same query.
 *   all words   a multi-word query matches only if EVERY word hits — "queen box
 *               spring" is not every queen-sized thing plus every box. A word
 *               hits a word that starts with it, so "fri" finds "fridge" while
 *               the client is still typing.
 *   ranked      exact    every word hits the item's own name
 *               synonym  every word hits the name and its search_terms together
 *               fuzzy    the same, allowing a typo or two per word — ONLY when
 *                        nothing matched exactly or by synonym, so a correctly
 *                        spelled query is never padded with near-misses.
 *               Within a tier: the whole query equal to a name or term first,
 *               then shorter names (the general item before its variants).
 *
 * search_terms come from inventory_catalog (migration 045); before that runs
 * they are absent, and search degrades to names only.
 */

export interface Searchable {
  id: string;
  name: string;
  searchTerms?: string[] | null;
}

export type MatchTier = 'exact' | 'synonym' | 'fuzzy';

/**
 * KEEP IN STEP with backend/src/lib/inventorySearchNormalize.ts, which counts
 * zero-result searches by this same key.
 */
export function normalize(s: string): string {
  return tokens(s).join(' ');
}

export function tokens(s: string): string[] {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')   // é -> e
    .replace(/["”“]/g, ' inch ')                         // 50" tv
    .replace(/[''`]/g, '')                               // banker's -> bankers
    .replace(/[^a-z0-9]+/g, ' ')                         // hyphens, slashes, commas
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(singular);
}

/** Plural to singular for the common English shapes. Not a stemmer — just enough. */
function singular(w: string): string {
  if (/\d/.test(w)) return w;
  // Three letters: "tvs" -> "tv", but not "gas" or "bus" (a vowel before the s).
  if (w.length === 3) return w.endsWith('s') && !/[aeiou]/.test(w[1]) ? w.slice(0, 2) : w;
  if (w.length < 3) return w;
  if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';      // shelves no, ponies -> pony
  if (/(xes|ches|shes|sses|zes)$/.test(w)) return w.slice(0, -2);         // boxes, benches, dishes
  if (w.endsWith('ves') && w.length > 4) return w.slice(0, -3) + 'f';      // shelves -> shelf
  if (w.endsWith('ss') || w.endsWith('us') || w.endsWith('is')) return w;  // glass, cactus, chassis
  if (w.endsWith('s')) return w.slice(0, -1);                             // sofas, chairs
  return w;
}

/**
 * Is the edit distance between a and b at most `max`? Insert, delete, replace,
 * and a swapped adjacent pair ("sfoa" for "sofa") each count as one. Stops as
 * soon as a whole row is over the limit.
 */
function within(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  let pp: number[] = [];                                   // row i - 2
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let d = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, pp[j - 2] + 1);
      cur[j] = d;
      if (d < rowMin) rowMin = d;
    }
    if (rowMin > max) return false;
    pp = prev;
    prev = cur;
  }
  return prev[b.length] <= max;
}

/** How many typos a word of this length may carry. Short words get none. */
const typoAllowance = (w: string) => (w.length < 4 ? 0 : w.length < 8 ? 1 : 2);

const hitsExactly = (q: string, words: string[]) => words.some((w) => w.startsWith(q));
const hitsFuzzily = (q: string, words: string[]) => {
  const max = typoAllowance(q);
  if (!max) return false;
  // Compare against the word, and against the word cut to the query's length so
  // a typo in a half-typed word still lands ("refridg" -> "refrigerator").
  return words.some((w) => within(q, w, max) || (w.length > q.length && within(q, w.slice(0, q.length), max)));
};

interface Prepared<T> { item: T; name: string[]; all: string[]; phrases: string[] }

/**
 * Build once per catalogue, not once per keystroke: 170 items, each split into
 * words, is the part worth caching. Callers memoise on the catalogue array.
 */
export function prepare<T extends Searchable>(items: T[]): Prepared<T>[] {
  return items.map((item) => {
    const name = tokens(item.name);
    const terms = (item.searchTerms ?? []).map(tokens);
    return {
      item,
      name,
      all: [...name, ...terms.flat()],
      phrases: [name.join(' '), ...terms.map((t) => t.join(' '))],
    };
  });
}

export interface Hit<T> { item: T; tier: MatchTier }

export function search<T extends Searchable>(prepared: Prepared<T>[], query: string): Hit<T>[] {
  const q = tokens(query);
  if (!q.length) return [];
  const phrase = q.join(' ');

  const rank = (p: Prepared<T>) => (p.phrases.includes(phrase) ? 0 : 1);
  const order = (a: Prepared<T>, b: Prepared<T>) =>
    rank(a) - rank(b) || a.item.name.length - b.item.name.length || a.item.name.localeCompare(b.item.name);

  const exact: Prepared<T>[] = [];
  const synonym: Prepared<T>[] = [];
  for (const p of prepared) {
    if (q.every((w) => hitsExactly(w, p.name))) exact.push(p);
    else if (q.every((w) => hitsExactly(w, p.all))) synonym.push(p);
  }
  if (exact.length || synonym.length) {
    return [
      ...exact.sort(order).map((p) => ({ item: p.item, tier: 'exact' as const })),
      ...synonym.sort(order).map((p) => ({ item: p.item, tier: 'synonym' as const })),
    ];
  }

  // Fallback: typos. A word may hit exactly or fuzzily; at least one must have
  // needed the fuzz, or it would have matched above.
  return prepared
    .filter((p) => q.every((w) => hitsExactly(w, p.all) || hitsFuzzily(w, p.all)))
    .sort(order)
    .map((p) => ({ item: p.item, tier: 'fuzzy' as const }));
}
